/**
 * Typing Effects Manager
 * 
 * Main coordinator for typing effects including audio, visual effects,
 * and user preferences. Integrates with Plate editor.
 */

import { PlateEditor } from '@udecode/plate/react';
import { TypingAudioManager, DEFAULT_TYPING_SOUNDS, AudioManagerOptions } from './typing-audio-manager';
import { TypingVisualEffectsManager, VisualEffectsConfig } from './typing-visual-effects';
import { CaretTracker } from './caret-tracker';

export interface TypingEffectsConfig {
  // Audio settings
  audioEnabled: boolean;
  currentSound: string;
  audioVolume: number;
  fastAudioMode: boolean;
  playbackRateVariation: boolean;

  // Visual effects settings
  visualEffectsEnabled: boolean;
  confettiEnabled: boolean;
  confettiType: 'standard' | 'stars' | 'emojis' | 'maple-leaves' | 'snow-prism' | 'hearts';
  confettiAmount: number;
  screenShakeEnabled: boolean;
  screenShakeIntensity: number;
  rainbowTextEnabled: boolean;
  progressCelebrationEnabled: boolean;

  // Performance settings
  throttleMs: number; // Minimum time between effects
}

export const DEFAULT_TYPING_EFFECTS_CONFIG: TypingEffectsConfig = {
  // Audio
  audioEnabled: false,
  currentSound: 'typewriter1',
  audioVolume: 0.3,
  fastAudioMode: true,
  playbackRateVariation: true,

  // Visual effects
  visualEffectsEnabled: false,
  confettiEnabled: true,
  confettiType: 'standard',
  confettiAmount: 3,
  screenShakeEnabled: false,
  screenShakeIntensity: 2,
  rainbowTextEnabled: false,
  progressCelebrationEnabled: true,

  // Performance
  throttleMs: 50 // Max 20 effects per second
};

export class TypingEffectsManager {
  private config: TypingEffectsConfig;
  private audioManager: TypingAudioManager;
  private visualEffectsManager: TypingVisualEffectsManager;
  private caretTracker: CaretTracker;
  private editor: PlateEditor | null = null;
  
  private lastEffectTime = 0;
  private isInitialized = false;
  private wordCount = 0;
  private lastWordCount = 0;
  private lastProgressCheck = 0;

  constructor(config: Partial<TypingEffectsConfig> = {}) {
    this.config = { ...DEFAULT_TYPING_EFFECTS_CONFIG, ...config };
    
    // Initialize managers
    this.audioManager = new TypingAudioManager({
      fastMode: this.config.fastAudioMode,
      volume: this.config.audioVolume,
      playbackRateVariation: this.config.playbackRateVariation
    });

    this.visualEffectsManager = new TypingVisualEffectsManager({
      confettiEnabled: this.config.confettiEnabled,
      confettiType: this.config.confettiType,
      confettiAmount: this.config.confettiAmount,
      screenShakeEnabled: this.config.screenShakeEnabled,
      screenShakeIntensity: this.config.screenShakeIntensity,
      rainbowTextEnabled: this.config.rainbowTextEnabled,
      progressCelebrationEnabled: this.config.progressCelebrationEnabled
    });

    this.caretTracker = new CaretTracker();
  }

  /**
   * Initialize the typing effects system
   * Must be called after user interaction
   */
  async initialize(editor: PlateEditor): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    this.editor = editor;
    this.caretTracker.setEditor(editor);

    // Initialize audio system
    await this.audioManager.initialize();

    // Load default sounds
    await this.loadDefaultSounds();

    this.isInitialized = true;
  }

  /**
   * Load default typing sounds
   */
  private async loadDefaultSounds(): Promise<void> {
    const loadPromises = DEFAULT_TYPING_SOUNDS
      .filter(sound => sound.url) // Skip 'none' sound
      .map(sound => this.audioManager.loadSound(sound));

    await Promise.all(loadPromises);
  }

  /**
   * Handle typing event - main entry point for effects
   */
  onTyping(event?: InputEvent): void {
    if (!this.isInitialized || !this.shouldTriggerEffects()) {
      return;
    }

    // Update last effect time
    this.lastEffectTime = Date.now();

    // Get caret position for visual effects
    const caretPosition = this.caretTracker.getCaretPosition();

    // Trigger audio effects
    if (this.config.audioEnabled && this.config.currentSound !== 'none') {
      this.audioManager.playSound(this.config.currentSound);
    }

    // Trigger visual effects
    if (this.config.visualEffectsEnabled) {
      this.visualEffectsManager.triggerTypingEffects(caretPosition);
    }

    // Check for progress milestones (throttled)
    const now = Date.now();
    // Only check progress milestones every 2 seconds to avoid recalculating the entire word count too often
    if (now - this.lastProgressCheck > 2000) {
      this.checkProgressMilestones();
      this.lastProgressCheck = now;
    }
  }

  /**
   * Check if effects should be triggered (throttling)
   */
  private shouldTriggerEffects(): boolean {
    const now = Date.now();
    return now - this.lastEffectTime >= this.config.throttleMs;
  }

  /**
   * Check for word count progress and trigger celebrations
   */
  private checkProgressMilestones(): void {
    if (!this.editor) return;

    try {
      // Get current word count from editor
      const content = this.editor.children;
      const newWordCount = this.calculateWordCount(content);
      
      // Check if word count increased significantly
      if (newWordCount > this.lastWordCount + 5) { // Every 5 words
        const progress = (newWordCount / 1000) * 100; // Assume 1000 word goal
        this.visualEffectsManager.triggerProgressCelebration(progress);
      }

      this.lastWordCount = newWordCount;
    } catch (error) {
      console.warn('Error checking progress milestones:', error);
    }
  }

  /**
   * Calculate word count from editor content
   */
  private calculateWordCount(content: any[]): number {
    try {
      const text = content
        .map(node => this.extractTextFromNode(node))
        .join(' ')
        .trim();

      if (!text) return 0;

      return text.split(/\s+/).filter(word => word.length > 0).length;
    } catch (error) {
      return 0;
    }
  }

  /**
   * Extract text from a Slate node recursively
   */
  private extractTextFromNode(node: any): string {
    if (typeof node === 'string') {
      return node;
    }

    if (node.text) {
      return node.text;
    }

    if (node.children && Array.isArray(node.children)) {
      return node.children.map(child => this.extractTextFromNode(child)).join('');
    }

    return '';
  }

  /**
   * Update configuration
   */
  updateConfig(newConfig: Partial<TypingEffectsConfig>): void {
    this.config = { ...this.config, ...newConfig };

    // Update audio manager
    this.audioManager.updateOptions({
      fastMode: this.config.fastAudioMode,
      volume: this.config.audioVolume,
      playbackRateVariation: this.config.playbackRateVariation
    });

    // Update visual effects manager
    this.visualEffectsManager.updateConfig({
      confettiEnabled: this.config.confettiEnabled,
      confettiType: this.config.confettiType,
      confettiAmount: this.config.confettiAmount,
      screenShakeEnabled: this.config.screenShakeEnabled,
      screenShakeIntensity: this.config.screenShakeIntensity,
      rainbowTextEnabled: this.config.rainbowTextEnabled,
      progressCelebrationEnabled: this.config.progressCelebrationEnabled
    });
  }

  /**
   * Get current configuration
   */
  getConfig(): TypingEffectsConfig {
    return { ...this.config };
  }

  /**
   * Enable/disable all effects
   */
  setEnabled(enabled: boolean): void {
    this.updateConfig({
      audioEnabled: enabled,
      visualEffectsEnabled: enabled
    });
  }

  /**
   * Cleanup resources
   */
  dispose(): void {
    this.audioManager.dispose();
    this.visualEffectsManager.dispose();
    this.caretTracker.dispose();
    this.isInitialized = false;
  }
}
